// @ts-nocheck
/**
 * RegistrationPaymentDialog — record money against a registration and read the
 * ledger behind its balance.
 *
 * Payments are transactions, not cells: every entry is a row in
 * registration_payments and a trigger re-derives the registration's paid /
 * refunded totals and payment status. Nothing here deletes an entry — a
 * correction voids it with a reason, and the void stays visible.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const METHODS = ["cash", "check", "venmo", "wire", "invoice", "other"];
const KINDS = [
  { value: "payment", label: "Payment" },
  { value: "refund", label: "Refund" },
  { value: "adjustment", label: "Adjustment" },
];

const asMoney = (v: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v ?? 0));

const num = (v: unknown) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function RegistrationPaymentDialog({
  registrationId,
  teamName,
  open,
  onOpenChange,
  onChanged,
}: {
  registrationId: string | null;
  teamName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState<any[]>([]);
  const [reg, setReg] = useState<any>(null);
  const [withdrawal, setWithdrawal] = useState<any>(null);
  const [sentByName, setSentByName] = useState("");


  const [kind, setKind] = useState("payment");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [receivedAt, setReceivedAt] = useState(today());
  const [reference, setReference] = useState("");

  const call = useCallback(
    async (body: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke("registration-payment", {
        body: { registration_id: registrationId, ...body },
      });
      if (error) {
        // invoke() flattens non-2xx into a generic message — read the real one.
        let detail = error.message;
        try {
          detail = (await (error as any).context?.text?.()) || detail;
          const parsed = JSON.parse(detail);
          detail = parsed.error ?? detail;
        } catch {
          /* keep whatever we have */
        }
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    [registrationId]
  );

  /**
   * A withdrawal refund is an agreed decision, not a transaction: nothing here
   * calls Stripe, so it stays owed until the organiser says they sent it.
   */
  const loadWithdrawal = useCallback(async () => {
    if (!registrationId) return;
    const { data } = await supabase
      .from("event_registration_withdrawals")
      .select(
        "id, refund_amount_cents, refund_processed_at, refund_marked_sent_by, credit_amount_cents, credit_issued_at, status"
      )
      .eq("registration_id", registrationId)
      .neq("status", "cancelled_by_user")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setWithdrawal(data ?? null);
    if (data?.refund_marked_sent_by) {
      const { data: p } = await supabase
        .from("profiles")
        .select("first_name, last_name, email")
        .eq("id", data.refund_marked_sent_by)
        .maybeSingle();
      setSentByName(
        [p?.first_name, p?.last_name].filter(Boolean).join(" ") || p?.email || "a staff member"
      );
    } else {
      setSentByName("");
    }
  }, [registrationId]);

  const refresh = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const data = await call({ action: "list" });
      setEntries(data?.entries ?? []);
      setReg(data?.registration ?? null);
      setMethod((m) => m || String(data?.registration?.payment_method_chosen ?? "").toLowerCase());
      await loadWithdrawal();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [registrationId, call, loadWithdrawal]);

  const markRefundSent = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("withdraw-registration", {
        body: { action: "mark_refund_sent", registration_id: registrationId },
      });
      const err = error?.message || (data as any)?.error;
      if (err) throw new Error(err);
      toast.success("Refund marked as sent.");
      await loadWithdrawal();
      onChanged?.();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open) {
      setKind("payment");
      setAmount("");
      setReference("");
      setReceivedAt(today());
      refresh();
    }
  }, [open, refresh]);

  const refundOwed = num(withdrawal?.refund_amount_cents) / 100;
  const creditAgreed = num(withdrawal?.credit_amount_cents) / 100;


  const due = num(reg?.total_amount_due) + num(reg?.late_fee_charged);
  const paid = num(reg?.total_amount_paid);
  const refunded = num(reg?.total_amount_refunded);
  const owed = Math.max(0, due - paid);

  const record = async () => {
    if (!amount.trim()) {
      toast.error("Enter an amount.");
      return;
    }
    setBusy(true);
    try {
      const data = await call({
        action: "record",
        kind,
        amount,
        method: method || undefined,
        received_at: receivedAt,
        reference: reference || undefined,
      });
      setEntries(data?.entries ?? []);
      setReg((prev: any) => ({ ...(prev ?? {}), ...(data?.totals ?? {}) }));
      setAmount("");
      setReference("");
      toast.success("Recorded in the payment ledger.");
      onChanged?.();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const voidEntry = async (entry: any) => {
    const reason = window.prompt(
      `Void ${asMoney(entry.amount)} ${entry.kind}? The entry stays in the ledger with your reason.`,
      ""
    );
    if (reason == null) return;
    if (!reason.trim()) {
      toast.error("A reason is required to void an entry.");
      return;
    }
    setBusy(true);
    try {
      const data = await call({ action: "void", entry_id: entry.id, void_reason: reason });
      setEntries(data?.entries ?? []);
      setReg((prev: any) => ({ ...(prev ?? {}), ...(data?.totals ?? {}) }));
      toast.success("Entry voided.");
      onChanged?.();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const stripeOwned =
    String(reg?.payment_method_chosen ?? "").toLowerCase() === "online" &&
    Boolean(reg?.stripe_payment_intent_id || reg?.stripe_session_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Payments — {teamName || "Registration"}</DialogTitle>
          <DialogDescription>
            Every entry is kept. Corrections void an entry with a reason rather than removing it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 rounded-md border p-3 text-sm sm:grid-cols-4">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Due</div>
            <div className="font-semibold">{asMoney(due)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Paid</div>
            <div className="font-semibold text-emerald-600">{asMoney(paid)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Refunded</div>
            <div className="font-semibold">{asMoney(refunded)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Owed</div>
            <div className="font-semibold text-amber-600">{asMoney(owed)}</div>
          </div>
        </div>

        {refundOwed > 0 &&
          (withdrawal?.refund_processed_at ? (
            <p className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              Refund of {asMoney(refundOwed)} sent{" "}
              {new Date(withdrawal.refund_processed_at).toLocaleDateString()}
              {sentByName ? ` by ${sentByName}` : ""}.
            </p>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <span>
                <strong>Refund owed: {asMoney(refundOwed)}</strong> — not yet sent. Send it yourself,
                then mark it here.
              </span>
              <Button size="sm" variant="outline" onClick={markRefundSent} disabled={busy}>
                Mark as sent
              </Button>
            </div>
          ))}

        {creditAgreed > 0 && (
          <p className="rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
            Credit agreed: {asMoney(creditAgreed)}. Credits aren't redeemable at checkout yet — you
            honour this manually on their next registration.
          </p>
        )}



        {stripeOwned ? (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            This registration was paid through Stripe. Adjust it in Stripe — recording it here would
            put your books out of sync with your processor.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-5">
            <div className="sm:col-span-1">
              <Label className="text-xs">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs">Amount</Label>
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs">Reference</Label>
              <Input
                placeholder="Check #, Venmo…"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
            <div className="sm:col-span-5">
              <Button onClick={record} disabled={busy}>
                {busy ? "Recording…" : "Record payment"}
              </Button>
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Date</th>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5 text-left">Method</th>
                <th className="px-2 py-1.5 text-left">Reference</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-2 py-3 text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-2 py-3 text-muted-foreground">
                    No payments recorded yet.
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className={`border-t ${e.voided_at ? "text-muted-foreground" : ""}`}>
                  <td className="px-2 py-1.5">
                    {new Date(e.received_at).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-1.5 capitalize">
                    {e.kind}
                    {e.voided_at && (
                      <span className="ml-1 rounded bg-muted px-1 text-[10px] uppercase">
                        voided
                      </span>
                    )}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums ${
                      e.voided_at ? "line-through" : ""
                    }`}
                  >
                    {asMoney(e.amount)}
                  </td>
                  <td className="px-2 py-1.5 capitalize">{e.method ?? "—"}</td>
                  <td className="px-2 py-1.5">
                    {e.reference || "—"}
                    {e.voided_at && e.void_reason && (
                      <div className="text-xs italic">Voided: {e.void_reason}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {!e.voided_at && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => voidEntry(e)}
                      >
                        Void
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
